if (!customElements.get('collection-inline-filters')) {
  customElements.define('collection-inline-filters', class extends HTMLElement {
    constructor() {
      super();
      ['onClick', 'onChange', 'onKey', 'onOutsideFocus', 'onPointerDown', 'sync', 'onBreakpoint', 'onScroll', 'updateHeaderOffset'].forEach(name => {
        this[name] = this[name].bind(this);
      });
    }

    connectedCallback() {
      this.scope = this.closest('[data-section-id]');
      this.form = this.scope.querySelector('#FacetFiltersForm');
      this.mobile = window.matchMedia('(max-width: 899px)');
      this.panel = this.querySelector('.collection-inline-filters__panel');
      this.options = this.querySelector('.collection-inline-filters__options');
      this.backdrop = this.querySelector('[data-inline-dismiss]');
      this.stickyHeader = this.closest('.collection__sticky-header');
      this.pageHeader = document.querySelector('.page-header');
      if (this.pageHeader) {
        this.headerObserver = new MutationObserver(this.onScroll);
        this.headerObserver.observe(this.pageHeader, { attributes: true, attributeFilter: ['class'] });
        this.headerSizeObserver = new ResizeObserver(this.onScroll);
        this.headerSizeObserver.observe(this.pageHeader);
      }
      this.anchor = document.createElement('span');
      this.anchor.className = 'collection-inline-filters__anchor';
      this.anchor.setAttribute('aria-hidden', 'true');
      this.closest('.collection__sticky-header').before(this.anchor);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('focusout', this.onOutsideFocus);
      this.addEventListener('pointerdown', this.onPointerDown, true);
      this.addEventListener('mousedown', this.onPointerDown, true);
      this.addEventListener('touchstart', this.onPointerDown, { capture: true, passive: true });
      document.addEventListener('facets:change', this.sync);
      this.mobile.addEventListener('change', this.onBreakpoint);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      const order = ['color', 'length', 'fabric', 'neckline', 'sleeve length'];
      const categories = [...this.querySelectorAll('[data-inline-category]:not([data-inline-category="sort"])')];
      categories.sort((a, b) => {
        const rank = button => {
          const label = button.childNodes[0].textContent.trim().toLowerCase();
          const index = order.indexOf(label);
          return index < 0 ? order.length : index;
        };
        return rank(a) - rank(b);
      }).forEach((button, index) => {
        button.dataset.baseOrder = index;
        this.querySelector('.collection-inline-filters__row').append(button);
      });
      this.restoreDraft();
      this.sync();
      this.onScroll();
    }

    disconnectedCallback() {
      this.close();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKey);
      this.removeEventListener('focusout', this.onOutsideFocus);
      this.removeEventListener('pointerdown', this.onPointerDown, true);
      this.removeEventListener('mousedown', this.onPointerDown, true);
      this.removeEventListener('touchstart', this.onPointerDown, true);
      document.removeEventListener('facets:change', this.sync);
      this.mobile.removeEventListener('change', this.onBreakpoint);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.headerObserver?.disconnect();
      this.headerSizeObserver?.disconnect();
      cancelAnimationFrame(this.headerFrame);
      this.headerFrame = null;
      this.anchor.remove();
    }

    inputs(name) {
      return [...this.form.elements].filter(input => name === 'price' ? input.name.startsWith('filter.v.price.') : input.name === name);
    }

    restoreDraft() {
      const draft = this.scope.inlineFilterDraft;
      if (!draft) return;
      delete this.scope.inlineFilterDraft;
      this.snapshot = new Map();
      draft.forEach(state => {
        const input = this.inputs(state.name).find(item => item.type === state.type &&
          (!['checkbox', 'radio', 'hidden'].includes(item.type) || item.value === state.value));
        if (!input) return;
        this.snapshot.set(input, { checked: input.checked, value: input.value, disabled: input.disabled });
        input.checked = state.checked;
        input.value = state.value;
        input.disabled = state.disabled;
      });
    }

    applyColor() {
      const name = this.active.dataset.inlineCategory;
      const params = new URLSearchParams(window.location.search);
      params.delete(name);
      const values = new Set(this.inputs(name).filter(input => !input.disabled &&
        (input.type === 'checkbox' ? input.checked : input.type === 'hidden')).map(input => input.value));
      values.forEach(value => params.append(name, value));
      const draft = [...(this.snapshot?.keys() || [])].filter(input => input.name !== name).map(input => ({
        name: input.name, type: input.type, value: input.value, checked: input.checked, disabled: input.disabled,
      }));
      this.close();
      if (draft.length) this.scope.inlineFilterDraft = draft;
      this.scope.inlineFilterReturnToHeading = true;
      FacetFiltersForm.renderPage(params.toString());
      this.sync();
    }

    hasColorFamilies() {
      return Boolean(this.scope.querySelector('collection-color-families'));
    }

    colorCount(selected) {
      const source = this.scope.querySelector('collection-color-families');
      if (!source) return selected.size;
      const labels = new Set();
      source?.families?.forEach(family => family.colors.forEach(color => {
        if (color.values.every(value => selected.has(value))) labels.add(source.normalize(color.label));
      }));
      return labels.size;
    }

    sync(event) {
      if (!this.form) return;
      const applied = new URLSearchParams(window.location.search);
      let total = 0;
      this.querySelectorAll('[data-inline-category]').forEach(button => {
        const name = button.dataset.inlineCategory;
        if (name === 'sort') {
          button.dataset.selected = String((applied.get('sort_by') || this.dataset.defaultSort) !== this.dataset.defaultSort);
          return;
        }
        const selected = new Set(applied.getAll(name).flatMap(value => value.split(',')).filter(Boolean));
        let count = name.includes('color-pattern') ? this.colorCount(selected) : selected.size;
        if (name === 'price') {
          const max = Number(this.form.querySelector('.f-price__slider')?.dataset.max);
          count = Number(this.inputs(name).some(input => {
            const value = applied.get(input.name);
            return value !== null && value !== '' &&
              (input.name.endsWith('gte') ? Number(value) > 0 : Number(value) < max);
          }));
        }
        total += count;
        button.dataset.selected = String(count > 0);
        button.style.order = (count > 0 ? -100 : 10) + Number(button.dataset.baseOrder);
        button.querySelector('[data-inline-count]').textContent = count ? `(${count})` : '';
      });
      const all = this.querySelector('[data-inline-total]');
      if (all) all.textContent = total ? `(${total})` : '';
    }

    open(button) {
      if (this.active === button) { this.close(true, true); return; }
      this.close(true, true);
      this.active = button;
      if (this.mobile.matches) this.classList.add('is-stuck');
      const name = button.dataset.inlineCategory;
      this.options.replaceChildren();
      const colorFamilies = name.includes('color-pattern') && this.hasColorFamilies();
      this.options.classList.toggle('collection-inline-filters__options--color', colorFamilies);
      this.options.classList.toggle('collection-inline-filters__options--swatches', name.includes('color-pattern') && !colorFamilies);
      this.options.classList.toggle('collection-inline-filters__options--icons', name !== 'sort' && !colorFamilies && this.inputs(name).some(input => input.closest('.filter-icon-option')));
      if (name === 'sort') {
        this.options.append(this.querySelector('[data-inline-sort-options]').content.cloneNode(true));
        this.options.querySelectorAll('input').forEach(input => {
          input.checked = input.value === this.inputs('sort_by')[0]?.value;
        });
      } else if (colorFamilies) {
        this.options.append(document.createElement('collection-filter-color-families'));
      } else {
        this.inputs(name).filter(input => input.type !== 'hidden').forEach((input, index) => {
          const label = document.createElement('label');
          label.className = 'collection-inline-filters__option';
          const proxy = input.cloneNode();
          proxy.removeAttribute('id');
          proxy.removeAttribute('name');
          proxy.removeAttribute('class');
          proxy.removeAttribute('tabindex');
          proxy.checked = input.checked;
          proxy.dataset.inlineInput = index;
          const text = document.createElement('span');
          text.textContent = input.dataset.label || input.getAttribute('aria-label') || name;
          if (name === 'price') {
            proxy.type = 'number';
            proxy.min = '0';
            text.textContent = input.name.endsWith('gte') ? this.dataset.priceFromLabel : this.dataset.priceToLabel;
          }
          const iconTile = input.closest('.filter-icon-option');
          if (iconTile) {
            label.className = 'filter-icon-option';
            label.classList.toggle('is-disabled', input.disabled);
            proxy.className = 'filter-icon-option__input';
            const caption = document.createElement('span');
            caption.className = 'filter-icon-option__label';
            caption.textContent = text.textContent;
            label.append(proxy, iconTile.querySelector('.filter-icon-option__icon').cloneNode(true), caption);
            this.options.append(label);
            return;
          }
          const swatch = name.includes('color-pattern') ? input.closest('label')?.querySelector('.filter__color-swatch') : null;
          if (swatch) {
            const dot = swatch.cloneNode(true);
            dot.classList.add('collection-inline-filters__swatch');
            label.classList.add('collection-inline-filters__option--swatch');
            proxy.setAttribute('aria-label', text.textContent);
            text.className = 'collection-inline-filters__swatch-label';
            text.setAttribute('aria-hidden', 'true');
            label.append(proxy, dot, text);
          } else {
            label.append(proxy, text);
          }
          this.options.append(label);
        });
      }
      this.snapshot ||= new Map();
      this.inputs(name === 'sort' ? 'sort_by' : name).forEach(input => {
        if (!this.snapshot.has(input)) this.snapshot.set(input, {
          checked: input.checked, value: input.value, disabled: input.disabled,
        });
      });
      const anchorTop = this.anchor.getBoundingClientRect().top;
      this.panel.hidden = false;
      this.backdrop.hidden = false;
      this.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      window.scrollTo({ top: window.scrollY + Math.max(0, anchorTop), behavior: 'instant' });
      this.onScroll();
      const row = this.querySelector('.collection-inline-filters__row');
      const bounds = row.getBoundingClientRect();
      const selected = button.getBoundingClientRect();
      const centered = row.scrollLeft + selected.left + selected.width / 2 - bounds.left - bounds.width / 2;
      row.scrollTo({
        left: Math.max(0, Math.min(centered, row.scrollWidth - row.clientWidth)),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }

    close(commit = false, keepDraft = false) {
      if (!this.active && !this.snapshot) return;
      if (!commit) this.snapshot?.forEach(({ checked, value, disabled }, input) => {
        if (!input.isConnected) return;
        input.checked = checked;
        input.value = value;
        input.disabled = disabled;
      });
      if (!keepDraft) this.snapshot = null;
      this.active?.setAttribute('aria-expanded', 'false');
      this.active = null;
      this.panel.hidden = true;
      this.backdrop.hidden = true;
      this.classList.remove('is-open');
      this.sync();
      this.onScroll();
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-drawer-all') && this.active?.dataset.inlineCategory.includes('color-pattern') && this.hasColorFamilies()) {
        this.applyColor();
        return;
      }
      if (button.hasAttribute('data-inline-category')) this.open(button);
      if (button.hasAttribute('data-inline-dismiss')) this.close();
      if (button.hasAttribute('data-inline-drawer')) {
        this.close();
        this.closest('collection-section').toggleDrawer();
      }
      if (button.hasAttribute('data-inline-reset') && this.active) {
        const name = this.active.dataset.inlineCategory;
        this.inputs(name === 'sort' ? 'sort_by' : name).forEach(input => {
          if (name === 'sort') input.value = this.dataset.defaultSort;
          else if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
          else if (input.type === 'hidden') input.disabled = true;
          else input.value = '';
        });
        this.options.querySelectorAll('input').forEach(input => {
          input.checked = name === 'sort' && input.value === this.dataset.defaultSort;
        });
        this.options.querySelector('collection-filter-color-families')?.refresh();
        this.sync();
      }
      if (button.hasAttribute('data-inline-done')) {
        this.close(true);
        this.form.closest('facet-filters-form').draftSnapshot = null;
        if (this.mobile.matches) this.scope.inlineFilterReturnToHeading = true;
        this.form.dispatchEvent(new Event('input', { bubbles: true }));
        this.sync();
      }
    }

    onChange(event) {
      if (!this.active) return;
      const name = this.active.dataset.inlineCategory;
      if (name.includes('color-pattern') && this.hasColorFamilies()) {
        this.applyColor();
        return;
      }
      if (name === 'sort') this.inputs('sort_by')[0].value = event.target.value;
      else if (event.target.hasAttribute('data-inline-input')) {
        const input = this.inputs(name).filter(item => item.type !== 'hidden')[Number(event.target.dataset.inlineInput)];
        input.checked = event.target.checked;
        if (input.type !== 'checkbox' && input.type !== 'radio') input.value = event.target.value;
      }
      this.sync();
    }

    onKey(event) {
      this.focusExitViaKeyboard = event.key === 'Tab';
      if (event.key !== 'Escape' || !this.active) return;
      event.preventDefault();
      event.stopPropagation();
      const button = this.active;
      this.close();
      button.focus();
    }

    onOutsideFocus(event) {
      if (!this.focusExitViaKeyboard) return;
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    }

    onPointerDown() { this.focusExitViaKeyboard = false; }

    onBreakpoint() {
      if (!this.mobile.matches) this.close();
      this.onScroll();
    }

    onScroll() {
      const state = this.pageHeader?.className;
      if (state !== this.headerState) {
        this.headerState = state;
        // Follow the header's existing 300 ms transition through its final frame.
        this.headerAnimationUntil = performance.now() + 350;
      }
      if (!this.headerFrame) this.headerFrame = requestAnimationFrame(this.updateHeaderOffset);
    }

    updateHeaderOffset() {
      this.headerFrame = null;
      if (!this.isConnected) return;
      const offset = this.mobile.matches && this.pageHeader?.classList.contains('sticky-enabled')
        ? Math.max(0, this.pageHeader.getBoundingClientRect().bottom) : 0;
      this.stickyHeader.style.setProperty('--collection-inline-top', `${offset}px`);
      this.classList.toggle('is-stuck', this.mobile.matches && (Boolean(this.active) || this.anchor.getBoundingClientRect().top <= offset));
      if (performance.now() < this.headerAnimationUntil) {
        this.headerFrame = requestAnimationFrame(this.updateHeaderOffset);
      }
    }
  });
}
